/**
 * Presentation primitives shared across all insight types. Every insight figure renders in the German
 * locale anchored to Europe/Berlin (the insight UI is hardcoded to `de-DE` everywhere, matching the
 * persisted German fallback the backend produced), so the locale/timezone, the number/date formatter
 * factories, the common euro + date formatters and the loosely-typed-field guards live here once instead
 * of being re-declared in every `*Presentation` / `*Insight.utils` file.
 */

export const INSIGHT_LOCALE = 'de-DE'
export const INSIGHT_TIMEZONE = 'Europe/Berlin'

/**
 * Builds a plain `de-DE` number formatter — e.g. `insightNumberFormatter({ maximumFractionDigits: 0 })`
 * turns `310` into `310`. The `Intl` formatter is built once and closed over, so the returned function is
 * cheap to call in render loops. Use `insightUnitFormatter` when the figure carries a unit.
 */
export const insightNumberFormatter = (options: Intl.NumberFormatOptions): ((value: number) => string) => {
  const formatter = new Intl.NumberFormat(INSIGHT_LOCALE, options)
  return value => formatter.format(value)
}

/**
 * Builds a `<number> <unit>` formatter in the German locale — e.g. `insightUnitFormatter('kW', { maximumFractionDigits: 2 })`
 * turns `774.4` into `774,40 kW`. The precision (and `signDisplay`) is the caller's, the locale and the
 * "value space unit" shape are shared.
 */
export const insightUnitFormatter = (unit: string, options: Intl.NumberFormatOptions): ((value: number) => string) => {
  const format = insightNumberFormatter(options)
  return value => `${format(value)} ${unit}`
}

/**
 * Builds an ISO-string → formatted-date function anchored to Europe/Berlin, so figures stay stable across
 * viewer timezones — e.g. `insightDateFormatter({ hour: '2-digit', minute: '2-digit' })` yields `14:45`.
 */
export const insightDateFormatter = (options: Intl.DateTimeFormatOptions): ((iso: string) => string) => {
  const formatter = new Intl.DateTimeFormat(INSIGHT_LOCALE, { ...options, timeZone: INSIGHT_TIMEZONE })
  return iso => formatter.format(new Date(iso))
}

/** Whole euros with a thousands separator and € suffix — e.g. `14.621 €`. */
export const formatInsightEur = insightUnitFormatter('€', { maximumFractionDigits: 2 })

/** A date as `dd.MM.yyyy`, Europe/Berlin — e.g. `31.07.2026`. */
export const formatInsightDate = insightDateFormatter({ day: '2-digit', month: '2-digit', year: 'numeric' })

const KWH_PER_MWH = 1000

const kwhFormatter = insightUnitFormatter('kWh', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const mwhFormatter = insightUnitFormatter('MWh', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * The one formatter set shared by every insight type. Most figures are formatted identically across
 * standby / peakload / baseload, and each insight just uses the subset it needs, so there is a single
 * object here instead of a near-duplicate one per category. All figures render in the German locale,
 * anchored to Europe/Berlin for dates/times.
 */
export const insightFormatters = {
  /** Whole euros with € suffix — e.g. `14.621 €`. */
  eur: formatInsightEur,
  /** Signed whole euros — e.g. `+1.234 €` (for the KPI-box deltas). */
  eurDelta: insightUnitFormatter('€', { maximumFractionDigits: 2, signDisplay: 'always' }),
  /** Effective energy price — e.g. `0,18 €/kWh`. */
  eurPerKwh: insightUnitFormatter('€/kWh', { minimumFractionDigits: 2, maximumFractionDigits: 3 }),
  /** Grid demand rate — e.g. `112,40 €/kW`. */
  eurPerKw: insightUnitFormatter('€/kW', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  /** Power in kW, two decimals — e.g. `774,40 kW`. */
  kw: insightUnitFormatter('kW', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  /** A signed percentage, two decimals — e.g. `+16,64 %`. */
  percent: insightUnitFormatter('%', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always' }),
  /** A signed whole percentage (rounded, no decimals) — e.g. `+111 %`. */
  percentWhole: insightUnitFormatter('%', { maximumFractionDigits: 0, signDisplay: 'always' }),
  /** Energy in kWh, two decimals — e.g. `20,46 MWh`. */
  kwh: (kwh: number): string => kwhFormatter(kwh),
  /** Energy in MWh, two decimals — e.g. `20,46 MWh`. */
  mwh: (kwh: number): string => mwhFormatter(kwh / KWH_PER_MWH),
  /** A whole number of hours — e.g. `310`. */
  hours: insightNumberFormatter({ maximumFractionDigits: 0 }),
  /** Hours with up to two decimals, for quarter-hour sums — e.g. `123,25`. */
  hoursPrecise: insightNumberFormatter({ maximumFractionDigits: 2 }),
  /** A whole number of days — e.g. `92`. */
  days: insightNumberFormatter({ maximumFractionDigits: 0 }),
  /** The extrapolation factor, two decimals — e.g. `3,97`. */
  factor: insightNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  /** A date as `dd.MM.yyyy`, Europe/Berlin — e.g. `31.07.2026`. */
  date: formatInsightDate,
  /** A time as `HH:mm`, Europe/Berlin — e.g. `14:45`. */
  time: insightDateFormatter({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  /** A four-digit year, Europe/Berlin — e.g. `2026`. */
  year: insightDateFormatter({ year: 'numeric' }),
}

/**
 * The last instant included in the window, for display only. `analysisWindowTo` is the exclusive end
 * (the start of the slot after the last reading), so a window that runs to 31.07 23:45 carries `to` =
 * 01.08 00:00; stepping back one millisecond keeps the printed end date on the last measured day (31.07).
 *
 * Note: the returned string is UTC (`toISOString()`), so it must only be rendered through
 * `formatInsightDate`, which re-applies the Europe/Berlin timezone. Do not use it for anything else.
 */
export const inclusiveWindowEnd = (toIso: string): string => new Date(new Date(toIso).getTime() - 1).toISOString()

const MS_PER_DAY = 24 * 60 * 60 * 1000
const DAYS_PER_YEAR = 365

/** Whole days spanned by the analysis window (`to` is the exclusive end, i.e. last slot + 15 min). */
export const analysisWindowDays = (fromIso: string, toIso: string): number =>
  Math.max(1, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / MS_PER_DAY))

/** The `365 / days` extrapolation factor; `1` for a full-year (or longer) window guard. */
export const yearlyExtrapolationFactor = (days: number): number => (days > 0 ? DAYS_PER_YEAR / days : 1)

/** Runtime guard: a finite number, else `null`. The insight `categoryData` bag is loosely typed. */
export const asFiniteNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

/**
 * Runtime guard for the insight's `analysisWindowFrom`/`analysisWindowTo` — mistyped as objects by the
 * generated OpenAPI schema but carrying an ISO date string at runtime. Returns `null` for anything that
 * is not a parseable date string, so callers fall back to the default presentation.
 */
export const asIsoString = (value: unknown): string | null => (typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null)
